<template>
    <div class="page">
        <kt-datatable
            :model="notificationModel"
            :data="rows"
            :filter="datatableFilter"
            :meta="{ tableClass: '-table-bordered', rowClass: (row) => getRowClass(row) }"
            :update-function="handleDatatableUpdate"
            :hide-search="false"
            storage-key="notifications-list"
            @cell-click="handleCellClick"
        >
            <template #header_select="scope">
                <div class="form-check">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        :checked="selectAll"
                        @change="toggleSelectAll"
                        @click.stop
                        title="Select all on this page"
                    />
                </div>
            </template>
            
            <template #view_select="scope">
                <div class="form-check">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        :checked="selectedRows.includes(scope.row.Id)"
                        @change="toggleRowSelection(scope.row.Id)"
                        @click.stop
                    />
                </div>
            </template>
            
            <template #view_actions="scope">
                <div class="btn-group btn-group-sm">
                    <button
                        class="btn btn-outline-primary btn-sm"
                        @click.stop="viewNotification(scope.row)"
                        title="View"
                    >
                        <Eye :size="14" />
                    </button>
                    <template v-if="!scope.row.IsDeleted">
                        <button
                            v-if="!scope.row.IsRead"
                            class="btn btn-outline-success btn-sm"
                            @click.stop="markAsRead(scope.row.Id)"
                            title="Mark as Read"
                        >
                            <Check :size="14" />
                        </button>
                        <button
                            v-else
                            class="btn btn-outline-secondary btn-sm"
                            @click.stop="markAsUnread(scope.row.Id)"
                            title="Mark as Unread"
                        >
                            <MailOpen :size="14" />
                        </button>
                        <button
                            class="btn btn-outline-danger btn-sm"
                            @click.stop="deleteNotification(scope.row.Id)"
                            title="Delete"
                        >
                            <Trash2 :size="14" />
                        </button>
                    </template>
                    <template v-else>
                        <button
                            class="btn btn-outline-success btn-sm"
                            @click.stop="restoreNotification(scope.row.Id)"
                            title="Restore"
                        >
                            <RotateCcw :size="14" />
                        </button>
                    </template>
                </div>
            </template>

            <template #filters="scope">
                <div class="col">
                    <div class="row">
                        <div class="col-auto">
                            <kt-autoselect
                                label="Mailbox"
                                :options="mailboxOptions"
                                display-by="label"
                                value-by="value"
                                :multiple="true"
                                :model-value="currentFilter.mailbox"
                                @update:model-value="(value: string[]) => setFilter({ mailbox: value })"
                                search-placeholder="Search mailbox..."
                            />
                        </div>
                        <div class="col-auto">
                            <kt-autoselect
                                label="Status"
                                :options="statusOptions"
                                display-by="label"
                                value-by="value"
                                :multiple="true"
                                :model-value="currentFilter.status"
                                @update:model-value="(value: string[]) => setFilter({ status: value })"
                                search-placeholder="Search status..."
                            />
                        </div>
                    </div>
                </div>
            </template>

            <template #after-search="scope">
                <div class="-btn-group">
                    <button @click="refresh()" class="btn btn-sm btn-outline-secondary me-2">
                        Refresh <i class="fa fa-sync"></i>
                    </button>
                    <button @click="markAllAsRead()" class="btn btn-sm btn-outline-success me-2">
                        Mark All Read <Check :size="14" />
                    </button>
                    <button @click="openNewMessage()" class="btn btn-sm btn-outline-primary">
                        New Message <i class="fa fa-plus"></i>
                    </button>
                </div>
            </template>

            <template #under-header>
                <div v-if="selectedRows.length > 0" class="mb-0 p-2 actions-row">
                    <div class="d-flex align-items-center gap-2">
                        <span class="fw-bold text-primary">{{ selectedRows.length }} selected</span>
                        <template v-if="!isDeletedFilterActive">
                            <button 
                                class="btn btn-sm btn-outline-success"
                                @click="markSelectedAsRead"
                            >
                                <Check :size="14" class="me-1" />
                                Mark Read
                            </button>
                            <button 
                                class="btn btn-sm btn-outline-secondary"
                                @click="markSelectedAsUnread"
                            >
                                <MailOpen :size="14" class="me-1" />
                                Mark Unread
                            </button>
                            <button 
                                class="btn btn-sm btn-outline-danger"
                                @click="deleteSelected"
                            >
                                <Trash2 :size="14" class="me-1" />
                                Delete
                            </button>
                        </template>
                        <template v-else>
                            <button 
                                class="btn btn-sm btn-outline-success"
                                @click="restoreSelected"
                            >
                                <RotateCcw :size="14" class="me-1" />
                                Restore
                            </button>
                        </template>
                        <button 
                            class="btn btn-sm btn-outline-secondary ms-auto"
                            @click="clearSelection"
                        >
                            Clear Selection
                        </button>
                    </div>
                </div>

                <!-- Filter Badges -->
                <div v-if="hasActiveFilters" class="d-flex flex-wrap gap-2 p-2">
                    <span class="text-muted small me-2">Active filters:</span>

                    <span v-if="currentFilter.mailbox.length > 0" class="badge bg-primary d-flex align-items-center">
                        Mailbox: {{ currentFilter.mailbox.map((v: string) => mailboxOptions.find(o => o.value === v)?.label).join(', ') }}
                        <button type="button" class="btn-close btn-close-white ms-2"
                                @click="setFilter({ mailbox: [] })"
                                style="font-size: 0.7em;"></button>
                    </span>

                    <span v-if="currentFilter.status.length > 0" class="badge bg-info d-flex align-items-center">
                        Status: {{ currentFilter.status.map((v: string) => statusOptions.find(o => o.value === v)?.label).join(', ') }}
                        <button type="button" class="btn-close btn-close-white ms-2"
                                @click="setFilter({ status: [] })"
                                style="font-size: 0.7em;"></button>
                    </span>

                    <span v-if="currentFilter.searchTerm" class="badge bg-secondary d-flex align-items-center">
                        Search: {{ currentFilter.searchTerm }}
                        <button type="button" class="btn-close btn-close-white ms-2"
                                @click="setFilter({ searchTerm: '' })"
                                style="font-size: 0.7em;"></button>
                    </span>

                    <button class="btn btn-sm btn-outline-secondary ms-0"
                            @click="clearAllFilters()">
                        Clear All
                    </button>
                </div>
            </template>

            <template #view_string="scope">
                <span v-if="scope.column.name === 'Subject'">
                    <span v-if="!scope.row.IsRead" class="badge bg-primary me-2">New</span>
                    <span v-if="scope.row.ConversationId" class="badge bg-secondary me-1" title="Part of a conversation"><MessageSquare :size="10" /></span>
                    <strong :class="{ 'fw-bold': !scope.row.IsRead }">{{ scope.row.Subject || '(No Subject)' }}</strong>
                </span>
                <span v-else-if="scope.column.name === 'NotificationType'">
                    <span :class="getTypeBadgeClass(scope.row.NotificationType)" class="badge">
                        {{ scope.row.NotificationType }}
                    </span>
                </span>
                <span v-else-if="scope.column.name === 'Reference'">
                    <span v-if="scope.row.RelatedEntityType && scope.row.RelatedEntityId">
                        <a
                            href="#"
                            class="text-decoration-none"
                            @click.prevent.stop="navigateToRelated(scope.row)"
                        >
                            {{ refTypeMap[scope.row.RelatedEntityType] || scope.row.RelatedEntityType }}
                            {{ scope.row.RelatedEntityId }}
                            <ExternalLink :size="11" class="ms-1 opacity-50" />
                        </a>
                    </span>
                    <span v-else class="text-muted">—</span>
                </span>
                <span v-else>
                    {{ scope.value }}
                </span>
            </template>
        </kt-datatable>

        <!-- View / Conversation Notification Modal -->
        <kt-modal v-model:visible="showViewModal" @dismiss="closeViewModal" title="Conversation" size="lg">
            <template #body>
                <div v-if="selectedNotification" class="email-view">

                    <!-- ── Email Subject Line ── -->
                    <div class="email-subject-bar px-1 pb-3 mb-0 border-bottom">
                        <div class="d-flex align-items-start gap-2">
                            <span :class="getTypeBadgeClass(selectedNotification.NotificationType)" class="badge mt-1" style="font-size:0.7rem;white-space:nowrap;">
                                {{ selectedNotification.NotificationType }}
                            </span>
                            <h5 class="mb-0 fw-semibold lh-sm" style="word-break:break-word;">{{ selectedNotification.Subject || '(No Subject)' }}</h5>
                        </div>
                    </div>

                    <!-- ── Thread (multiple messages) or single message ── -->
                    <div class="email-thread mt-0" style="max-height:400px;overflow-y:auto;">

                        <!-- Loop for threaded view -->
                        <template v-if="threadMessages.length > 1">
                            <div
                                v-for="(msg, idx) in threadMessages"
                                :key="msg.Id"
                                class="email-message border-bottom py-3 px-1"
                                :class="msg.Id === selectedNotification.Id ? 'email-message--active' : ''"
                            >
                                <!-- Sender row -->
                                <div class="d-flex align-items-start gap-3 mb-2">
                                    <div class="email-avatar flex-shrink-0" :title="msg.SenderName || 'System'">
                                        {{ avatarInitials(msg.SenderName) }}
                                    </div>
                                    <div class="flex-grow-1 min-w-0">
                                        <div class="d-flex justify-content-between align-items-baseline gap-2">
                                            <span class="fw-semibold text-truncate">{{ msg.SenderName || 'System' }}</span>
                                            <small class="text-muted text-nowrap flex-shrink-0">{{ formatDate(msg.CreatedDate) }}</small>
                                        </div>
                                        <div class="text-muted small">
                                            <span v-if="msg.RecipientName">To: {{ msg.RecipientName }}</span>
                                            <span v-if="msg.IsPrivate" class="badge bg-warning text-dark ms-2" style="font-size:0.65rem;">
                                                <Lock :size="10" class="me-1" />Private
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <!-- Body -->
                                <div class="email-body ps-5" v-html="msg.Body"></div>
                            </div>
                        </template>

                        <!-- Single message view -->
                        <template v-else>
                            <div class="email-message py-3 px-1">
                                <!-- Sender row -->
                                <div class="d-flex align-items-start gap-3 mb-3">
                                    <div class="email-avatar flex-shrink-0" :title="selectedNotification.SenderName || 'System'">
                                        {{ avatarInitials(selectedNotification.SenderName) }}
                                    </div>
                                    <div class="flex-grow-1 min-w-0">
                                        <div class="d-flex justify-content-between align-items-baseline gap-2">
                                            <span class="fw-semibold">{{ selectedNotification.SenderName || 'System' }}</span>
                                            <small class="text-muted text-nowrap flex-shrink-0">{{ formatDate(selectedNotification.CreatedDate) }}</small>
                                        </div>
                                        <div class="text-muted small">
                                            <span v-if="selectedNotification.RecipientName">To: {{ selectedNotification.RecipientName }}</span>
                                            <span v-if="selectedNotification.IsPrivate" class="badge bg-warning text-dark ms-2" style="font-size:0.65rem;">
                                                <Lock :size="10" class="me-1" />Private
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <!-- Body -->
                                <div class="email-body ps-5" v-html="selectedNotification.Body"></div>
                            </div>
                        </template>

                    </div>

                    <!-- ── Reply composer ── -->
                    <div v-if="!showReplyBox" class="pt-3 border-top">
                        <button class="btn btn-sm btn-outline-primary" @click="showReplyBox = true">
                            <Reply :size="14" class="me-1" />Reply
                        </button>
                    </div>
                    <div v-else class="pt-3 border-top">
                        <div class="email-reply-header d-flex align-items-center gap-2 mb-2">
                            <Reply :size="14" class="text-muted" />
                            <small class="text-muted fw-semibold">Reply to {{ selectedNotification.SenderName || 'System' }}</small>
                            <button class="btn-close ms-auto" style="font-size:0.6rem;" @click="showReplyBox = false; replyBody = ''"></button>
                        </div>
                        <textarea
                            class="form-control form-control-sm mb-2"
                            rows="4"
                            v-model="replyBody"
                            placeholder="Write your reply..."
                            @keydown.ctrl.enter="sendReply"
                            autofocus
                        ></textarea>
                        <div class="d-flex justify-content-between align-items-center">
                            <small class="text-muted">Ctrl+Enter to send</small>
                            <div class="d-flex gap-2">
                                <button class="btn btn-sm btn-outline-secondary" @click="showReplyBox = false; replyBody = ''">Cancel</button>
                                <button class="btn btn-sm btn-primary" @click="sendReply" :disabled="!replyBody || isSendingReply">
                                    <span v-if="isSendingReply"><span class="spinner-border spinner-border-sm me-1"></span>Sending...</span>
                                    <span v-else><Send :size="13" class="me-1" />Send</span>
                                </button>
                            </div>
                        </div>
                    </div>

                </div>
            </template>
            <template #footer>
                <div class="d-flex justify-content-between w-100 align-items-center">
                    <div>
                        <button 
                            v-if="selectedNotification && selectedNotification.RelatedEntityType && selectedNotification.RelatedEntityId"
                            class="btn btn-sm btn-outline-primary"
                            @click="navigateToRelated(selectedNotification)"
                        >
                            <ExternalLink :size="13" class="me-1" />
                            View {{ refTypeMap[selectedNotification?.RelatedEntityType] || selectedNotification?.RelatedEntityType }}
                        </button>
                    </div>
                    <button class="btn btn-secondary" @click="closeViewModal">Close</button>
                </div>
            </template>
        </kt-modal>

        <!-- New Message Modal -->
        <kt-modal v-model:visible="showNewMessageModal" title="New Message" size="lg">
            <template #body>
                <div class="mb-3">
                    <label class="form-label">Recipients</label>
                    <tech-multi-ac
                        v-model="newMessage.recipientTechIds"
                        placeholder="Select technicians..."
                    />
                </div>
                <div class="mb-3">
                    <label class="form-label">Subject</label>
                    <input type="text" class="form-control form-control-sm" placeholder="Message Subject" v-model="newMessage.subject" />
                </div>
                <div class="mb-3">
                    <label class="form-label">Message</label>
                    <textarea class="form-control form-control-sm" placeholder="Message Body" rows="6" v-model="newMessage.body"></textarea>
                </div>
            </template>
            <template #footer>
                <button class="btn btn-secondary" @click="showNewMessageModal = false">Cancel</button>
                <button class="btn btn-primary" @click="sendMessage" :disabled="isSending">
                    <span v-if="isSending">Sending...</span>
                    <span v-else>Send Message</span>
                </button>
            </template>
        </kt-modal>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import KtDatatable from "@/components/UI/KtDatatable.vue";
import KtAutoselect from "@/components/UI/KtAutoselect.vue";
import KtModal from "@/components/UI/KtModal.vue";
import TechMultiAc from "@/components/Techs/TechMultiAc.vue";
import { Eye, Check, Trash2, MailOpen, RotateCcw, ExternalLink, MessageSquare, Reply, Send, Lock } from 'lucide-vue-next';
import ApiService from "@/services/ApiService";
import Cache from "@/services/Cache";
import Alert from "@/services/Alert";
import Toast from "@/services/Toast";
import Emitter from "@/services/Emitter";
import { usePermissions } from '@/composables';

const router = useRouter();
const route = useRoute();
const { isAdmin } = usePermissions();

// ===== FILTER OPTIONS =====

const statusOptions = [
    { label: 'All', value: 'all' },
    { label: 'Unread', value: 'unread' },
    { label: 'Read', value: 'read' },
    { label: 'Deleted', value: 'deleted' }
];

const mailboxOptions = [
    { label: 'All Messages', value: 'all' },
    { label: 'Inbox', value: 'inbox' },
    { label: 'Sent', value: 'sent' }
];

// ===== FILTER STATE =====

interface NotificationListFilter {
    mailbox: string[];
    status: string[];
    searchTerm: string;
    page: number;
    size: number;
    sort: Array<Record<string, 'ASC' | 'DESC'>>;
}

const getDefaultFilter = (): NotificationListFilter => ({
    mailbox: [],
    status: [],
    searchTerm: '',
    page: 0,
    size: 25,
    sort: []
});

const currentFilter = ref<NotificationListFilter>(getDefaultFilter());

// Datatable filter (derived from currentFilter — KtDatatable reads this)
const datatableFilter = computed(() => ({
    page: currentFilter.value.page,
    size: currentFilter.value.size,
    total: total.value,
    search: currentFilter.value.searchTerm,
    sort: currentFilter.value.sort
}));

const total = ref(0);
const rows = ref<any[]>([]);

// ===== UI STATE =====

const selectedRows = ref<number[]>([]);
const selectAll = ref(false);
const showViewModal = ref(false);
const showNewMessageModal = ref(false);
const selectedNotification = ref<any>(null);
const isSending = ref(false);
const threadMessages = ref<any[]>([]);
const showReplyBox = ref(false);
const replyBody = ref('');
const isSendingReply = ref(false);

const newMessage = ref({
    recipientTechIds: [] as number[],
    subject: '',
    body: ''
});


const refTypeMap = ref<any>({
    Ask: 'Request',
    Request: 'Work Order'
});

// ===== DATATABLE MODEL =====

const notificationModel = [
    {
        name: "Select",
        label: "",
        type: "select",
        width: 40,
        locked: true,
        draggable: false
    },
    {
        name: "Id",
        label: "ID",
        display: false
    },
    {
        name: "Subject",
        label: "Subject",
        display: true,
        type: "string",
        sortable: true
    },
    {
        name: "SenderName",
        label: "From",
        display: true,
        type: "string",
        width: 150
    },
    {
        name: "RecipientName",
        label: "To",
        display: true,
        type: "string",
        width: 150
    },
    {
        name: "NotificationType",
        label: "Type",
        display: true,
        type: "string",
        width: 120
    },
    {
        name: "Reference",
        label: "Reference",
        display: true,
        type: "string",
        width: 130
    },
    {
        name: "CreatedDate",
        label: "Date",
        display: true,
        type: "date",
        sortable: true,
        width: 150
    },
    {
        name: "IsRead",
        label: "Read",
        display: false
    },
    {
        name: "Actions",
        label: "Actions",
        type: "actions",
        width: 120,
        locked: true
    }
];

// ===== COMPUTED =====

const isDeletedFilterActive = computed(() => currentFilter.value.status.includes('deleted'));

// ===== HELPERS =====

const getRowClass = (row: any) => {
    if (row.IsDeleted) return 'row-deleted';
    return row.IsRead ? '' : 'table-info';
};

const getTypeBadgeClass = (type: string) => {
    switch (type) {
        case 'Message': return 'bg-primary';
        case 'Update': return 'bg-info';
        case 'Alert': return 'bg-warning';
        case 'System': return 'bg-secondary';
        default: return 'bg-secondary';
    }
};

const formatDate = (date: string) => {
    if (!date) return '';
    return new Date(date).toLocaleString();
};

// ===== URL & FILTER UTILITIES =====

const parseSortFromUrl = (sortParam?: string): Array<Record<string, 'ASC' | 'DESC'>> => {
    if (!sortParam) return [];
    return sortParam.split(',').map(sortField => {
        const trimmed = sortField.trim();
        if (trimmed.startsWith('-')) {
            return { [trimmed.substring(1)]: 'DESC' as const };
        }
        return { [trimmed]: 'ASC' as const };
    });
};

const parseUrlToFilter = (): NotificationListFilter => {
    const q = route.query;
    return {
        mailbox: q.mailbox ? (q.mailbox as string).split(',') : [],
        status: q.status ? (q.status as string).split(',') : [],
        searchTerm: (q.searchTerm as string) || '',
        page: parseInt((q.page as string) || '0'),
        size: parseInt((q.size as string) || '25'),
        sort: parseSortFromUrl(q.sort as string)
    };
};

const buildUrlFromFilter = (filter: NotificationListFilter): string => {
    const params = new URLSearchParams();

    if (filter.mailbox && filter.mailbox.length > 0) params.set('mailbox', filter.mailbox.join(','));
    if (filter.status && filter.status.length > 0) params.set('status', filter.status.join(','));
    if (filter.searchTerm) params.set('searchTerm', filter.searchTerm);

    if (filter.sort && filter.sort.length > 0) {
        const sortString = filter.sort.map(sortObj => {
            const [column, direction] = Object.entries(sortObj)[0];
            return direction === 'DESC' ? `-${column}` : column;
        }).join(',');
        params.set('sort', sortString);
    }

    params.set('page', filter.page.toString());
    if (filter.size !== 25) params.set('size', filter.size.toString());

    return params.toString();
};

const buildApiParams = (filter: NotificationListFilter) => {
    const params: any = {
        page: filter.page + 1,
        pageSize: filter.size
    };

    // Mailbox filter — use first selected value or 'all'
    if (filter.mailbox && filter.mailbox.length > 0) {
        params.mailbox = filter.mailbox.length === 1 ? filter.mailbox[0] : filter.mailbox.join(',');
    } else {
        params.mailbox = 'all';
    }

    // Status filter — support multiple selections
    const statuses = filter.status || [];
    if (statuses.includes('unread')) {
        params.isRead = false;
    } else if (statuses.includes('read')) {
        params.isRead = true;
    }
    if (statuses.includes('deleted')) {
        params.deletedOnly = true;
    }

    if (filter.searchTerm) {
        params.searchTerm = filter.searchTerm;
    }

    if (filter.sort && filter.sort.length > 0) {
        const orderByParts = filter.sort.map(sortObj => {
            const [column, direction] = Object.entries(sortObj)[0];
            return direction === 'DESC' ? `-${column}` : column;
        });
        params.orderBy = orderByParts.join(',');
    }

    return params;
};

// ===== API INTEGRATION =====

const loadData = async () => {
    try {
        const params = buildApiParams(currentFilter.value);
        console.debug('Notification API params:', params);

        const response = await ApiService.getNotifications(params);

        if (response.Status === 1) {
            rows.value = response.Data || [];
            total.value = response.Meta?.TotalCount || 0;
        }
    } catch (error) {
        console.error('Error loading notifications:', error);
        Toast.error('Failed to load notifications');
    }
};

// ===== FILTER UPDATE HANDLERS =====

const handleDatatableUpdate = (filterUpdate: any) => {
    console.debug('Datatable filter update:', filterUpdate);

    if (filterUpdate.page !== undefined) currentFilter.value.page = filterUpdate.page;
    if (filterUpdate.size !== undefined) currentFilter.value.size = filterUpdate.size;
    if (filterUpdate.sort !== undefined) currentFilter.value.sort = filterUpdate.sort || [];
    if (filterUpdate.search !== undefined) currentFilter.value.searchTerm = filterUpdate.search;

    // Save to cache
    Cache.set('notification-list-filter', currentFilter.value);

    // Update URL
    const urlParams = buildUrlFromFilter(currentFilter.value);
    const newUrl = `/notification/list${urlParams ? '?' + urlParams : ''}`;
    router.replace(newUrl);

    refresh();
};

const setFilter = (filterData: Partial<NotificationListFilter>) => {
    console.debug("setFilter:", filterData);

    const tempFilter = { ...currentFilter.value };

    if (filterData.mailbox !== undefined) tempFilter.mailbox = Array.isArray(filterData.mailbox) ? filterData.mailbox : (filterData.mailbox ? [filterData.mailbox] : []);
    if (filterData.status !== undefined) tempFilter.status = Array.isArray(filterData.status) ? filterData.status : (filterData.status ? [filterData.status] : []);
    if (filterData.searchTerm !== undefined) tempFilter.searchTerm = filterData.searchTerm;
    if (filterData.sort !== undefined) tempFilter.sort = filterData.sort || [];

    // Reset to first page when filter changes
    tempFilter.page = 0;

    // 1. Update currentFilter
    currentFilter.value = tempFilter;

    // 2. Save to cache
    Cache.set('notification-list-filter', currentFilter.value);

    // 3. Update URL
    const urlParams = buildUrlFromFilter(tempFilter);
    const newUrl = `/notification/list${urlParams ? '?' + urlParams : ''}`;
    router.replace(newUrl);

    // 4. Reload data
    refresh();
};

const hasActiveFilters = computed(() => {
    return currentFilter.value.mailbox.length > 0 ||
           currentFilter.value.status.length > 0 ||
           currentFilter.value.searchTerm !== '';
});

const clearAllFilters = () => {
    setFilter(getDefaultFilter());
};

// ===== INITIALIZATION =====

const hasUrlParams = (): boolean => {
    const q = route.query;
    return !!(q.mailbox || q.status || q.searchTerm || q.sort);
};

const hasNonEmptyFilterValues = (filters: any): boolean => {
    if (!filters) return false;
    if (filters.mailbox && filters.mailbox.length > 0) return true;
    if (filters.status && filters.status.length > 0) return true;
    if (filters.searchTerm) return true;
    return false;
};

const initializeFilter = async (): Promise<void> => {
    if (hasUrlParams()) {
        // URL has params — use those
        currentFilter.value = parseUrlToFilter();
        Cache.set('notification-list-filter', currentFilter.value);
        console.debug('Initialized from URL params:', currentFilter.value);
    } else {
        // Blank URL — try cached filters
        const stored = Cache.get('notification-list-filter');
        if (stored && hasNonEmptyFilterValues(stored)) {
            currentFilter.value = {
                ...getDefaultFilter(),
                ...stored,
                page: 0
            };
            console.debug('Initialized from cache:', currentFilter.value);
        } else {
            currentFilter.value = getDefaultFilter();
            console.debug('Initialized with defaults');
        }

        // Update URL to reflect current state
        const urlParams = buildUrlFromFilter(currentFilter.value);
        const newUrl = `/notification/list${urlParams ? '?' + urlParams : ''}`;
        router.replace(newUrl);
    }
};

// ===== ACTIONS =====

const refresh = () => {
    loadData();
    Emitter.emit('notification:refresh');
};

const handleCellClick = (row: any, column: any) => {
    if (column.name !== 'Select' && column.name !== 'Actions') {
        viewNotification(row);
    }
};

const closeViewModal = () => {
    showViewModal.value = false;
    showReplyBox.value = false;
    replyBody.value = '';
    threadMessages.value = [];
};

const viewNotification = async (notification: any) => {
    selectedNotification.value = notification;
    showReplyBox.value = false;
    replyBody.value = '';
    threadMessages.value = [];
    showViewModal.value = true;

    if (!notification.IsRead) {
        await markAsRead(notification.Id);
        notification.IsRead = true;
    }

    // Load conversation thread if this notification has one
    if (notification.ConversationId) {
        try {
            const response = await ApiService.getNotificationThread(notification.ConversationId);
            if (response?.Status === 1) {
                threadMessages.value = response.Data || [];
            }
        } catch (error) {
            console.error('Error loading thread:', error);
        }
    } else {
        threadMessages.value = [notification];
    }
};

const sendReply = async () => {
    if (!replyBody.value || !selectedNotification.value) return;
    isSendingReply.value = true;
    try {
        // Ensure the original notification has a ConversationId (creates one if missing)
        let conversationId = selectedNotification.value.ConversationId;
        const payload: any = {
            Body: replyBody.value,
            Subject: selectedNotification.value.Subject ? `Re: ${selectedNotification.value.Subject}` : 'Re: (No Subject)',
            NotificationType: selectedNotification.value.NotificationType || 'Message',
            RelatedEntityType: selectedNotification.value.RelatedEntityType,
            RelatedEntityId: selectedNotification.value.RelatedEntityId,
            // Reply to original sender
            RecipientUserId: selectedNotification.value.SenderUserId,
        };

        if (conversationId) {
            payload.ConversationId = conversationId;
        } else {
            payload.ParentNotificationId = selectedNotification.value.Id;
        }

        await ApiService.saveNotification(payload);
        replyBody.value = '';
        showReplyBox.value = false;
        closeViewModal();
        refresh();
        Emitter.emit('notification:refresh');
    } catch (error) {
        Toast.error('Failed to send reply');
    } finally {
        isSendingReply.value = false;
    }
};

const markAsRead = async (id: number) => {
    try {
        await ApiService.markNotificationRead(id);
        const notification = rows.value.find(n => n.Id === id);
        if (notification) {
            notification.IsRead = true;
        }
        Emitter.emit('notification:refresh');
    } catch (error) {
        console.error('Error marking as read:', error);
    }
};

const markAsUnread = async (id: number) => {
    try {
        await ApiService.markNotificationUnread(id);
        const notification = rows.value.find(n => n.Id === id);
        if (notification) {
            notification.IsRead = false;
        }
        Emitter.emit('notification:refresh');
    } catch (error) {
        console.error('Error marking as unread:', error);
        Toast.error('Failed to mark as unread');
    }
};

const markAllAsRead = async () => {
    try {
        await ApiService.markAllNotificationsRead();
        rows.value.forEach(n => n.IsRead = true);
        Emitter.emit('notification:refresh');
    } catch (error) {
        Toast.error('Failed to mark all as read');
    }
};

const markSelectedAsRead = async () => {
    for (const id of selectedRows.value) {
        await markAsRead(id);
    }
    clearSelection();
};

const markSelectedAsUnread = async () => {
    for (const id of selectedRows.value) {
        await markAsUnread(id);
    }
    clearSelection();
};

const deleteNotification = async (id: number) => {
    Alert.confirm('Are you sure you want to delete this notification?', async () => {
        try {
            await ApiService.deleteNotification(id);
            rows.value = rows.value.filter(n => n.Id !== id);
            Emitter.emit('notification:refresh');
        } catch (error) {
            Toast.error('Failed to delete notification');
        }
    }, () => {});
};

const deleteSelected = async () => {
    Alert.confirm(`Delete ${selectedRows.value.length} notifications?`, async () => {
        try {
            await ApiService.deleteMultipleNotifications(selectedRows.value);
            rows.value = rows.value.filter(n => !selectedRows.value.includes(n.Id));
            clearSelection();
            Emitter.emit('notification:refresh');
        } catch (error) {
            Toast.error('Failed to delete notifications');
        }
    }, () => {});
};

const restoreNotification = async (id: number) => {
    try {
        await ApiService.restoreNotification(id);
        rows.value = rows.value.filter(n => n.Id !== id);
        Emitter.emit('notification:refresh');
    } catch (error) {
        Toast.error('Failed to restore notification');
    }
};

const restoreSelected = async () => {
    Alert.confirm(`Restore ${selectedRows.value.length} notifications?`, async () => {
        try {
            await ApiService.restoreMultipleNotifications(selectedRows.value);
            rows.value = rows.value.filter(n => !selectedRows.value.includes(n.Id));
            clearSelection();
            Emitter.emit('notification:refresh');
        } catch (error) {
            Toast.error('Failed to restore notifications');
        }
    }, () => {});
};

const toggleSelectAll = () => {
    if (selectAll.value) {
        selectedRows.value = [];
    } else {
        selectedRows.value = rows.value.map(r => r.Id);
    }
    selectAll.value = !selectAll.value;
};

const toggleRowSelection = (id: number) => {
    const index = selectedRows.value.indexOf(id);
    if (index > -1) {
        selectedRows.value.splice(index, 1);
    } else {
        selectedRows.value.push(id);
    }
};

const clearSelection = () => {
    selectedRows.value = [];
    selectAll.value = false;
};

const openNewMessage = () => {
    newMessage.value = {
        recipientTechIds: [],
        subject: '',
        body: ''
    };
    showNewMessageModal.value = true;
};

const sendMessage = async () => {
    if (newMessage.value.recipientTechIds.length === 0) {
        Toast.error('Please select at least one recipient');
        return;
    }

    isSending.value = true;
    try {
        // Send separate message to each recipient
        for (const techId of newMessage.value.recipientTechIds) {
            await ApiService.saveNotification({
                RecipientUserId: techId,
                Subject: newMessage.value.subject,
                Body: newMessage.value.body,
                NotificationType: 'Message'
            });
        }
        
        const count = newMessage.value.recipientTechIds.length;
        Toast.success(`Message sent to ${count} recipient${count > 1 ? 's' : ''}`);
        
        showNewMessageModal.value = false;
        refresh();
    } catch (error) {
        Toast.error('Failed to send message');
    } finally {
        isSending.value = false;
    }
};

const navigateToRelated = (notification: any) => {
    const type = notification.RelatedEntityType?.toLowerCase();
    const id = notification.RelatedEntityId;

    showViewModal.value = false;

    console.debug("message ref type: " + type);

    switch (type) {
        case 'request':
            Emitter.emit("edit:job", id);
            break;

        case 'ask':
            Emitter.emit("open:requestForm", id);
            break;
    }

    /* TODO - will need to fix soon but these types are starting to get confusing ...
    switch (type) {
        case 'job':
        case 'workorder':
            Emitter.emit('edit:job', id);
            break;
        case 'task':
        case 'time':
            Emitter.emit('open:taskForm', id);
            break;
        case 'request':
            router.push(`/request/edit/${id}`);
            break;
        default:
            console.log('Unknown entity type:', type);
    }
    */
};

const avatarInitials = (name: string | null | undefined): string => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// ===== LIFECYCLE =====

onMounted(async () => {
    await initializeFilter();
    loadData();
});
</script>

<style scoped>
.actions-row {
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
}

:deep(.row-deleted td) {
    text-decoration: line-through;
    opacity: 0.6;
}

/* Email-style notification view */
.email-view {
    font-size: 0.925rem;
}

.email-subject-bar h5 {
    font-size: 1.05rem;
    color: #1a1a2e;
}

.email-avatar {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: linear-gradient(135deg, #4361ee, #7b2ff7);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.03em;
    flex-shrink: 0;
    user-select: none;
}

.email-message {
    transition: background 0.15s;
}

.email-message--active {
    background-color: #f5f8ff;
}

.email-body {
    color: #2d2d2d;
    line-height: 1.65;
    word-break: break-word;
}

.email-thread {
    scrollbar-width: thin;
    scrollbar-color: #dee2e6 transparent;
}

.email-reply-header {
    border-radius: 4px;
    padding: 4px 0;
}
</style>
